import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { api, apiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import {
  QrCode, Camera, CheckCircle2, XCircle, AlertTriangle, User, Zap, Clock, Type,
  MapPin, RefreshCw, ShieldAlert, KeySquare, Wallet, IndianRupee, Link2, Send,
} from "lucide-react";

export default function AdminDeskCheckin() {
  const [scanning, setScanning] = useState(false);
  const [manualToken, setManualToken] = useState("");
  const [preview, setPreview] = useState(null);   // booking preview from lookup
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);
  const [collectMethod, setCollectMethod] = useState("cash");
  const scannerRef = useRef(null);

  const loadRecent = () => api.get("/desk/recent-checkins").then((r) => setRecent(r.data || []));
  useEffect(() => { loadRecent(); }, []);

  const lookup = async (raw) => {
    const token = (raw || "").trim();
    if (!token) return;
    setError(null); setPreview(null);
    try {
      const { data } = await api.get(`/desk/booking-lookup?token=${encodeURIComponent(token)}`);
      setPreview({ ...data, _token: token });
    } catch (e) {
      setError(e.response?.data?.detail || "Booking not found");
    }
  };

  const startScan = async () => {
    setScanning(true); setError(null); setPreview(null);
    // Give React one frame so `#qr-reader-desk` exists and is laid-out with real width.
    // Without this, html5-qrcode measures a 0-px container on mobile → blank video.
    await new Promise((r) => setTimeout(r, 120));
    try {
      // Kill any zombie instance first (previous run that didn't clean up).
      if (scannerRef.current) {
        try { await scannerRef.current.stop(); } catch { /* ignore */ }
        try { await scannerRef.current.clear(); } catch { /* ignore */ }
        scannerRef.current = null;
      }
      const html5QrCode = new Html5Qrcode("qr-reader-desk", { verbose: false });
      scannerRef.current = html5QrCode;

      // Compute qrbox as 75% of the container's smaller side so both portrait
      // and landscape mobile videos frame the QR nicely.
      const el = document.getElementById("qr-reader-desk");
      const side = el ? Math.min(el.clientWidth, el.clientHeight, 320) : 240;
      const box = Math.max(160, Math.floor(side * 0.75));
      const config = { fps: 10, qrbox: { width: box, height: box }, aspectRatio: 1.0 };

      const onScan = async (decodedText) => {
        try { await html5QrCode.stop(); } catch { /* ignore */ }
        setScanning(false);
        await lookup(decodedText);
      };

      // Try the rear camera first; on iOS Safari `facingMode` occasionally fails
      // silently → fall back to enumerated devices.
      try {
        await html5QrCode.start({ facingMode: { exact: "environment" } }, config, onScan, () => {});
      } catch {
        try {
          await html5QrCode.start({ facingMode: "environment" }, config, onScan, () => {});
        } catch {
          const cams = await Html5Qrcode.getCameras();
          if (!cams || cams.length === 0) throw new Error("No cameras found");
          const back = cams.find((c) => /back|rear|environment/i.test(c.label)) || cams[cams.length - 1];
          await html5QrCode.start(back.id, config, onScan, () => {});
        }
      }
    } catch (e) {
      console.warn("QR scanner start failed:", e);
      toast.error(`Camera unavailable · ${e?.message || "check browser permissions"}. Type the token manually.`);
      setScanning(false);
    }
  };
  const stopScan = async () => {
    try { await scannerRef.current?.stop(); } catch { /* ignore */ }
    try { await scannerRef.current?.clear(); } catch { /* ignore */ }
    scannerRef.current = null;
    setScanning(false);
  };

  // Cleanup on unmount so the camera light doesn't stay on when the desk
  // manager navigates away.
  useEffect(() => {
    return () => {
      const s = scannerRef.current;
      if (s) {
        s.stop().catch(() => {}).finally(() => { try { s.clear(); } catch { /* ignore */ } });
      }
    };
  }, []);

  const checkIn = async (force = false) => {
    if (!preview) return;
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/desk/checkin", { token: preview._token, force });
      setPreview({ ...data, _token: preview._token });
      toast.success(`Checked in · ${data.party}`);
      loadRecent();
    } catch (e) {
      setError(e.response?.data?.detail || "Check-in failed");
    } finally { setBusy(false); }
  };

  const clearAll = () => { setPreview(null); setError(null); setManualToken(""); };

  const checkOut = async (force = false) => {
    if (!preview) return;
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/desk/checkout", { token: preview._token, force });
      setPreview({ ...data, _token: preview._token });
      const ot = data.overtime_minutes || 0;
      toast.success(`Checked out · ${data.party} · ${data.minutes_played || 0} min${ot > 0 ? ` (${ot} min overtime)` : ""}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Check-out failed");
    } finally { setBusy(false); }
  };

  const collectBalance = async () => {
    if (!preview) return;
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/desk/collect-balance", {
        token: preview._token,
        method: collectMethod,
      });
      setPreview({ ...data, _token: preview._token });
      toast.success(`Balance collected · ${formatINR(preview.balance_due)}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Balance collection failed");
    } finally { setBusy(false); }
  };

  const sendBalanceLink = async (resend = false) => {
    if (!preview?.id) return;
    setBusy(true);
    try {
      const { data } = await api.post("/bookings/balance-link", { booking_id: preview.id, resend });
      setPreview({ ...preview, balance_payment_link_url: data.short_url, balance_payment_link_id: data.id });
      toast.success(data.reused ? "Existing link fetched" : "Balance link sent via WhatsApp");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send balance link");
    } finally { setBusy(false); }
  };

  const copyLink = async () => {
    const url = preview?.balance_payment_link_url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  return (
    <div className="p-5 md:p-8" data-testid="admin-desk-checkin">
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-widest brand-orange">// Desk</div>
        <h1 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tighter mt-1">Desk Check-in</h1>
        <p className="text-sm text-white/60 mt-1">Scan or type a booking QR to verify the slot and mark the guest / member as checked in.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* LEFT: scanner + manual */}
        <div className="lg:col-span-2 space-y-4">
          <div className="border border-white/12 bg-[#131A2B] p-5" data-testid="scanner-card">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// Camera scanner</div>
              {!scanning ? (
                <button onClick={startScan} data-testid="desk-start-scan"
                  className="bg-brand-blue text-white px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5">
                  <Camera size={12} /> Start
                </button>
              ) : (
                <button onClick={stopScan} data-testid="desk-stop-scan"
                  className="border border-red-400 text-red-400 px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest">
                  Stop
                </button>
              )}
            </div>
            <div className="relative w-full aspect-square bg-black/40 border border-white/10 overflow-hidden">
              {/* html5-qrcode injects the <video> here. Keep it EMPTY when scanning so
                  our placeholder icon doesn't clash with the video sizing. */}
              <div id="qr-reader-desk" className="absolute inset-0" />
              {!scanning && (
                <div className="absolute inset-0 grid place-items-center pointer-events-none">
                  <QrCode size={64} className="text-white/15" />
                </div>
              )}
            </div>
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); lookup(manualToken); }}
            className="border border-white/12 bg-[#131A2B] p-5"
            data-testid="manual-entry"
          >
            <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-3">// Type token manually</div>
            <div className="flex gap-2">
              <input
                value={manualToken}
                onChange={(e) => setManualToken(e.target.value)}
                placeholder="e.g. 4hK9nZa1Xp2q"
                data-testid="desk-manual-token"
                className="flex-1 bg-black/40 border border-white/15 focus:border-brand-blue outline-none px-3 py-2.5 text-sm font-mono"
              />
              <button type="submit" data-testid="desk-lookup-btn"
                className="bg-brand-orange text-black px-4 py-2.5 font-bold text-xs uppercase tracking-widest inline-flex items-center gap-1.5">
                <Type size={12} /> Look up
              </button>
            </div>
            <div className="mt-2 text-[10px] text-white/40">Full token or first 8+ chars work.</div>
          </form>
        </div>

        {/* CENTER: preview & check-in */}
        <div className="lg:col-span-3 space-y-3">
          {error && (
            <div className="border border-red-500/60 bg-red-950/40 p-4 flex gap-3 items-start" data-testid="desk-error">
              <XCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
              <div>
                <div className="font-display text-sm font-black uppercase text-red-300">Rejected</div>
                <div className="text-sm text-white/80 mt-1">{error}</div>
                {preview && (
                  <button onClick={() => checkIn(true)} disabled={busy}
                    data-testid="desk-force-btn"
                    className="mt-3 border border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-black px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5">
                    <ShieldAlert size={11} /> Override &amp; Check In
                  </button>
                )}
              </div>
            </div>
          )}

          {preview && !error && (
            <div className={`border p-5 ${
              preview.checked_in_at ? "border-green-500/60 bg-green-950/20" : "border-brand-blue/60 bg-brand-blue/8"
            }`} data-testid="desk-preview">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">Booking preview</div>
                  <div className="font-display text-2xl font-black uppercase mt-1">{preview.service_name}</div>
                </div>
                {preview.checked_in_at ? (
                  <div className="text-[10px] font-mono uppercase tracking-widest text-green-400 border border-green-500/60 px-2 py-1 inline-flex items-center gap-1">
                    <CheckCircle2 size={11} /> Checked in
                  </div>
                ) : (
                  <div className="text-[10px] font-mono uppercase tracking-widest brand-blue border border-brand-blue/60 px-2 py-1">READY</div>
                )}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
                <Row icon={User}     label="Guest / Member" value={preview.party}
                     hint={preview.is_member ? "Member reservation" : "Guest booking"} />
                <Row icon={MapPin}   label="Resource" value={preview.resource || "—"} />
                <Row icon={Clock}    label="Time" value={`${preview.date} · ${preview.start_hour}:00 → ${preview.start_hour + (preview.duration || 1)}:00`} />
                <Row icon={KeySquare} label="Token" value={<span className="font-mono text-xs brand-blue">{preview.qr_token?.slice(0, 16)}…</span>} />
              </div>

              {preview.checked_in_at && (
                <div className="mt-4 text-[11px] font-mono text-green-300/80">
                  Checked in {new Date(preview.checked_in_at).toLocaleString()} by {preview.checked_in_by_name || "staff"}
                </div>
              )}

              {/* Balance-due panel */}
              {(preview.balance_due || 0) > 0 && (
                <div className="mt-4 border border-yellow-500/60 bg-yellow-950/25 p-4" data-testid="balance-due-panel">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-yellow-300 flex items-center gap-1.5">
                        <Wallet size={12} /> Outstanding Balance
                      </div>
                      <div className="font-display text-2xl font-black text-yellow-200 mt-1" data-testid="balance-due-amount">
                        {formatINR(preview.balance_due)}
                      </div>
                      <div className="text-[10px] font-mono text-white/50 mt-1">
                        Paid so far {formatINR(preview.amount_paid || 0)} / Total {formatINR(preview.total || 0)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={collectMethod}
                        onChange={(e) => setCollectMethod(e.target.value)}
                        data-testid="balance-method"
                        className="bg-black/40 border border-white/15 py-2 px-3 text-xs font-mono uppercase tracking-widest"
                      >
                        <option value="cash">Cash</option>
                        <option value="upi">UPI</option>
                        <option value="card">Card</option>
                      </select>
                      <button
                        onClick={collectBalance}
                        disabled={busy}
                        data-testid="collect-balance-btn"
                        className="bg-yellow-400 text-black px-4 py-2 font-bold text-[11px] uppercase tracking-widest inline-flex items-center gap-1.5 hover:bg-yellow-300 disabled:opacity-50"
                      >
                        <IndianRupee size={12} /> {busy ? "Collecting…" : `Collect ${formatINR(preview.balance_due)}`}
                      </button>
                    </div>
                  </div>

                  {/* Razorpay balance link — send / copy */}
                  <div className="mt-3 border-t border-yellow-500/30 pt-3 flex flex-wrap items-center gap-2">
                    {preview.balance_payment_link_url ? (
                      <>
                        <a
                          href={preview.balance_payment_link_url} target="_blank" rel="noreferrer"
                          data-testid="balance-link-open"
                          className="text-[10px] font-mono uppercase tracking-widest text-yellow-200 underline break-all inline-flex items-center gap-1.5"
                        >
                          <Link2 size={11} /> {preview.balance_payment_link_url}
                        </a>
                        <button
                          type="button" onClick={copyLink} data-testid="balance-link-copy"
                          className="text-[10px] font-mono uppercase tracking-widest border border-yellow-500/40 text-yellow-300 px-2 py-1 hover:bg-yellow-500 hover:text-black"
                        >
                          Copy
                        </button>
                        <button
                          type="button" onClick={() => sendBalanceLink(true)} data-testid="balance-link-resend"
                          disabled={busy}
                          className="text-[10px] font-mono uppercase tracking-widest border border-yellow-500/40 text-yellow-300 px-2 py-1 hover:bg-yellow-500 hover:text-black disabled:opacity-40 inline-flex items-center gap-1"
                        >
                          <RefreshCw size={11} /> Regenerate
                        </button>
                      </>
                    ) : (
                      <button
                        type="button" onClick={() => sendBalanceLink(false)} data-testid="balance-link-send"
                        disabled={busy}
                        className="text-[10px] font-mono uppercase tracking-widest border border-yellow-500/40 text-yellow-300 px-3 py-1.5 hover:bg-yellow-500 hover:text-black inline-flex items-center gap-1.5 disabled:opacity-40"
                      >
                        <Send size={11} /> Send Razorpay Balance Link via WhatsApp
                      </button>
                    )}
                  </div>
                </div>
              )}
              {(preview.balance_due || 0) === 0 && preview.payment_status === "paid" && !preview.checked_in_at && (
                <div className="mt-3 text-[10px] font-mono text-green-300 inline-flex items-center gap-1.5">
                  <CheckCircle2 size={11} /> Fully paid
                </div>
              )}

              <div className="mt-5 flex gap-2 flex-wrap">
                {!preview.checked_in_at && (
                  <button onClick={() => checkIn(false)} disabled={busy}
                    data-testid="desk-checkin-btn"
                    className="flex-1 bg-brand-orange text-black py-3 font-bold text-xs uppercase tracking-widest hover:bg-white disabled:opacity-40 inline-flex items-center justify-center gap-2">
                    <CheckCircle2 size={14} /> {busy ? "Checking in…" : "Check In"}
                  </button>
                )}
                {preview.checked_in_at && !preview.checked_out_at && (
                  <button onClick={() => checkOut(false)} disabled={busy}
                    data-testid="desk-checkout-btn"
                    className="flex-1 bg-green-400 text-black py-3 font-bold text-xs uppercase tracking-widest hover:bg-white disabled:opacity-40 inline-flex items-center justify-center gap-2">
                    <CheckCircle2 size={14} /> {busy ? "Checking out…" : "Check Out"}
                  </button>
                )}
                {preview.checked_out_at && (
                  <div className="flex-1 border border-green-500/40 bg-green-950/30 py-3 px-3 text-center">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-green-300">Session Closed</div>
                    <div className="text-xs mt-1 font-mono">
                      Played {preview.minutes_played || 0} min
                      {preview.overtime_minutes > 0 && <span className="text-red-300"> · overtime {preview.overtime_minutes} min</span>}
                    </div>
                  </div>
                )}
                <button onClick={clearAll} data-testid="desk-clear-btn"
                  className="border border-white/15 px-4 py-3 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5">
                  <RefreshCw size={11} /> Next
                </button>
              </div>
            </div>
          )}

          {!preview && !error && (
            <div className="border border-dashed border-white/15 p-10 text-center text-white/40 text-sm">
              Scan a QR or type the token to see booking details.
            </div>
          )}

          {/* Recent check-ins */}
          <div className="border border-white/10 mt-3" data-testid="recent-checkins">
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
              <Zap size={12} className="brand-orange" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/60">Recent check-ins</div>
            </div>
            {recent.length === 0 ? (
              <div className="text-white/40 text-xs text-center py-6">No check-ins yet today.</div>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {recent.map((r) => (
                    <tr key={r.id} className="border-b border-white/6 last:border-b-0">
                      <td className="px-3 py-2 text-white/70">
                        <div className="font-medium">{r.party}</div>
                        <div className="text-[11px] text-white/50">{r.service_name} · {r.resource} · {r.date} @ {r.start_hour}:00</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-[10px] text-green-300">
                        <div className="inline-flex items-center gap-1"><CheckCircle2 size={11} /> {new Date(r.checked_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, hint }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon size={14} className="brand-blue shrink-0 mt-0.5" />
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">{label}</div>
        <div className="font-medium">{value}</div>
        {hint && <div className="text-[10px] text-white/40 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}
